import { storage } from '@/lib/storage';
import { Customer } from '@/types';
import { nanoid } from 'nanoid';
import { vehicleService } from './vehicleService';

const CUSTOMERS_KEY = 'customers';

export const customerService = {
  getAll: (): Customer[] => {
    return storage.get<Customer[]>(CUSTOMERS_KEY) || [];
  },

  getById: (id: string): Customer | null => {
    const customers = customerService.getAll();
    return customers.find(c => c.id === id) || null;
  },

  search: (query: string): Customer[] => {
    const customers = customerService.getAll();
    const lowerQuery = query.toLowerCase();
    const matchingVehicleCustomerIds = new Set(
      vehicleService.search(query).map(vehicle => vehicle.customer_id)
    );

    return customers.filter(c =>
      c.name.toLowerCase().includes(lowerQuery) ||
      c.phone.includes(query) ||
      (c.email && c.email.toLowerCase().includes(lowerQuery)) ||
      matchingVehicleCustomerIds.has(c.id)
    );
  },

  create: (data: Omit<Customer, 'id' | 'created_at' | 'updated_at' | 'loyalty_points'>): Customer => {
    const customers = customerService.getAll();
    const newCustomer: Customer = {
      ...data,
      id: nanoid(),
      loyalty_points: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    customers.push(newCustomer);
    storage.set(CUSTOMERS_KEY, customers);
    return newCustomer;
  },

  update: (id: string, data: Partial<Customer>): Customer | null => {
    const customers = customerService.getAll();
    const index = customers.findIndex(c => c.id === id);
    if (index === -1) return null;
    
    customers[index] = {
      ...customers[index],
      ...data,
      id: customers[index].id,
      created_at: customers[index].created_at,
      updated_at: new Date().toISOString(),
    };
    storage.set(CUSTOMERS_KEY, customers);
    return customers[index];
  },

  delete: (id: string): boolean => {
    const customers = customerService.getAll();
    const filtered = customers.filter(c => c.id !== id);
    if (filtered.length === customers.length) return false;
    storage.set(CUSTOMERS_KEY, filtered);
    return true;
  },
};
